/**
 * Form Component for our Return details sidebar that
 * lets users edit the details of a customer like
 * email, name and address.
 *
 * @component
 */

import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
} from 'antd'
import axios from 'axios'
import React,{ Component } from 'react'
import {
  frequentlyUsedCountries,
  nonFrequentlyUsedCountries,
  USStateNames,
} from '../../../../constants/constants'

const { Option } = Select
const FormItem = Form.Item
// eslint-disable-next-line no-unused-vars
const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 20 },
  },
}

class ReturnDetailsCustomerForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      visible: true,
      loading: false,
      updateDetailsOnShopify: true,
    }
  }
  formRef = React.createRef();

  componentDidMount() {
    this.setState({
      ...this.props.returnObject,
    },() => {
      this.formRef.current.setFieldsValue({
        customerEmail: this.state.customerEmail,
        shippingFirstName: this.state.shippingFirstName,
        shippingLastName: this.state.shippingLastName,
        shippingCompany: this.state.shippingCompany,
        shippingAddress1: this.state.shippingAddress1,
        shippingAddress2: this.state.shippingAddress2,
        shippingCity: this.state.shippingCity,
        shippingZip: this.state.shippingZip,
        shippingPhone: this.state.shippingPhone,
        shippingCountryCode: this.state.shippingCountryCode,
        shippingProvinceCode: this.state.shippingProvinceCode,
      })
    })
  }

  handleCancel = () => {
    this.props.toggleModal()
  };

  handleCountryChange = (countryCode) => {
    this.setState({
      shippingCountryCode: countryCode,
    })
  };

  toggleUpdateDetailsOnShopify = () => {
    this.setState({
      updateDetailsOnShopify: !this.state.updateDetailsOnShopify,
    })
  };

  handleSubmit = async () => {
    try {
      const values = await this.formRef.current.validateFields()

      this.setState({ loading: true })

      const response = await axios.put('/api/returns/customer',{
        returnId: this.state.id,
        updateDetailsOnShopify: this.state.updateDetailsOnShopify,
        formValues: values,
      })

      if (response.data.status === 'error') {
        this.setState({ loading: false })
        return message.error(response.data.error,10)
      }

      this.setState({ loading: false })
      this.props.handleGetReturn()
      this.props.toggleModal()
      return message.success('Customer details updated successfully.',5)
    } catch (err) {
      this.setState({ loading: false })

      if (err.errorFields) {
        return message.error(
          'There is something wrong with the data you entered. Please check again.',
          5
        )
      }

      return message.error(
        'Error updating Customer details. Please try again.',
        5
      )
    }
  };


  getCountriesList = () => {
    let countriesList = []
    frequentlyUsedCountries.forEach((country) => {
      countriesList.push(<Option value={country.code}>{country.name}</Option>)
    })

    const disabledoption = (
      <Option value="disabled" disabled={true}>
        ---------
      </Option>
    )
    countriesList.push(disabledoption)
    nonFrequentlyUsedCountries.forEach((country) => {
      countriesList.push(<Option value={country.code}>{country.name}</Option>)
    })

    return countriesList
  };

  getUSStateList = () => {
    const USStateList = USStateNames.map((state) => {
      return <Option value={state.code}>{state.name}</Option>
    })

    return USStateList
  };

  render() {
    return (
      <Modal
        title="Update customer shipping details"
        visible={true}
        onCancel={this.handleCancel}
        footer={[
          <Button key="back" onClick={this.handleCancel} className="u-marginTop--xs">
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={this.state.loading}
            onClick={this.handleSubmit}
            className="u-marginTop--xs"
          >
            Update
          </Button>,
        ]}
      >
        <Form ref={this.formRef}>
          <Row
            type="flex"
            justify="space-around"
            align="middle"
            className="dashboard-content"
          >
            <Col span={24}>
              <h4>Email Address</h4>
              <FormItem name="customerEmail">
                <Input
                  placeholder="For delivering instructions via email"
                  className="u-marginBottom--sm"
                  required
                />
              </FormItem>
              <h4>First name</h4>
              <FormItem name="shippingFirstName">
                <Input
                  className="u-marginBottom--sm"
                  required
                />
              </FormItem>
              <h4>Last name</h4>
              <FormItem name="shippingLastName">
                <Input
                  className="u-marginBottom--sm"
                  required
                />
              </FormItem>
              <h4>Company</h4>
              <FormItem name="shippingCompany">
                <Input
                  placeholder="(optional)"
                  className="u-marginBottom--sm"
                />
              </FormItem>
              <h4>Street address</h4>
              <FormItem name="shippingAddress1">
                <Input
                  placeholder="Street and number, P.O. box, c/o."
                  className="u-marginBottom--sm"
                  required
                />
              </FormItem>
              <h4>Apartment, suite, unit, building, floor, etc. (optional)</h4>
              <FormItem name="shippingAddress2">
                <Input
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  className="u-marginBottom--sm"
                />
              </FormItem>
              <h4>City</h4>
              <FormItem name="shippingCity">
                <Input
                  className="u-marginBottom--sm"
                  required
                />
              </FormItem>
              <h4>ZIP code</h4>
              <FormItem name="shippingZip">
                <Input
                  className="u-marginBottom--sm"
                  required
                />
              </FormItem>
              <h4>Phone number</h4>
              <FormItem required name="shippingPhone">
                <Input
                  placeholder={this.props.returnObject?.shippingMethod === 'methodLabelCreation' ? 'Required for label creation' : '(optional) May be used to assist delivery'}
                  className="u-marginBottom--sm"
                  required={this.props.returnObject?.shippingMethod === 'methodLabelCreation'}
                />
              </FormItem>
              <h4>Country</h4>
              <FormItem name="shippingCountryCode">
                <Select
                  defaultValue={this.state.shippingCountryCode}
                  placeholder="Select country"
                  onChange={this.handleCountryChange.bind(this)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input,option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  className="u-marginBottom--sm"
                  required
                >
                  {this.getCountriesList()}
                </Select>
              </FormItem>
              <h4>State</h4>
              {this.state.shippingCountryCode !== 'US' ? (
                <FormItem name="shippingProvinceCode">
                  <Input
                    placeholder="(optional)"
                  />
                </FormItem>
              ) : (
                <FormItem name="shippingProvinceCode">
                  <Select
                    defaultValue={this.state.shippingProvinceCode}
                    placeholder="State / Province / Region"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input,option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    className="u-marginBottom--sm"
                    required
                  >
                    {this.getUSStateList()}
                  </Select>
                </FormItem>
              )}
              <FormItem
                name="updateDetailsOnShopify"
                valuePropName="checked"
                initialValue={this.state.updateDetailsOnShopify}
              >
                <Checkbox
                  onChange={this.toggleUpdateDetailsOnShopify}
                >
                  Update customer shipping details in Shopify order
                </Checkbox>
              </FormItem>
            </Col>
          </Row>
        </Form>
      </Modal>
    )
  }

}

export default ReturnDetailsCustomerForm
